import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Disposable, disposeHammers, disposeResizeObserver, registerHammerForDisposal, setDisposable } from "@cervval.js/client-html5-core/src/components/disposable";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { AcceptedHtmlElementContent, KeyboardInput, findElementByRef, findNearestMatch, ignoreResizeObserverLoopLimit } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { NumberTools } from "@cervval.js/common/tools";
import Hammer from "hammerjs";
import { CjsSmartTable } from "../smartTable/smartTable";

export interface SmartScrollBarOnScrollEvent {
    /** New index of firstDisplayed item */
    index: number;
}
export interface SmartScrollBarOptions {

    /* Number that represents the element index displayed at the top of the smartList */
    firstDisplayedIndex: number;

    /** Set a default minHeight in px in case there is a lot of elements to display */
    minHeight: number;

    /* Total number of elements available */
    totalElementsCount: number;

    /* Number of elements that can be displayed at the same time */
    selectedElementsCount: ValueOrFunction<number>;

    /** Wheel event display update debounce value */
    wheelScrollEventDebounce: number;

    /** Element that will be handle by smartScrollBar */
    elementToHandle?: HTMLElement;

    /** 
     * Observe resize is set to false by default because most components using SmartScrollBar already have a resize observer that refreshes the scrollbar 
     * Cannot be changed after init
     */
    observeResize?: boolean;

    /** Do not install nor fire mobile pan events listeners, mostly used when parent scrolltable has no height constraint or non-smart table in smartSelect*/
    doNotUseMobilePanEvents?: () => boolean;
}

export const defaultSmartScrollBarOptions: SmartScrollBarOptions = {

    /* Number that represents the element index displayed at the top of the smartList */
    firstDisplayedIndex: 0,

    /** If there is a lot of elements we use a default value 15px */
    minHeight: 15,

    /* Total number of elements available */
    totalElementsCount: 0,

    /* Number of elements that can be displayed at the same time */
    selectedElementsCount: 0,

    /** Wheel event display update debounce value */
    wheelScrollEventDebounce: 20

};
@CustomElement({
    name: "cjs-smart-scroll-bar",
    style: require("./smartScrollBar.scss"),
    template: require("./smartScrollBar.html")
})
export class CjsSmartScrollBar extends WebComponent implements Disposable {

    @TypedWebComponentOptions(defaultSmartScrollBarOptions)
    public readonly options!: WebComponentOptions<SmartScrollBarOptions>;

    @TypedHtmlElements()
    public readonly components!: {
        scrollDiv: HTMLDivElement,
        thumb: HTMLDivElement
    };

    /** Refresh index of SmartScrollBar is trigger */
    @Event(NoBubblesEventInit)
    public refreshIndexEvent!: EventHandler<SmartScrollBarOnScrollEvent>;

    // Wheel event can scroll
    private _canWheelEventScroll = true;

    /** Store Height of scroll div (avoid getComputedStyle every refresh loop) */
    private _scrollDivHeight = 0;

    /** Store height of thumb (avoid getComputedStyle every refresh loop) */
    private _thumbHeight = 0;

    /** Should we move thumb to firstDisplayIndex position */
    private _moveThumbToFirstDisplayIndexPosition = true;

    /** Store previous first index displayed */
    private _previousFirstDisplayedIndex = 0;

    /** elements to dispose when calling dispose function */
    protected _elementsToDispose: {
        hammers?: HammerManager[],
        resizeObserver?: ResizeObserver
    } = {};
    protected _isDisposed = false;

    constructor(options?: Partial<SmartScrollBarOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        setDisposable(this);
        this.options.setWithoutRefresh(options);

        if (this.options.firstDisplayedIndex) {
            this._previousFirstDisplayedIndex = this.options.firstDisplayedIndex;
        }

        this._keyDownHandler = this._keyDownHandler.bind(this);
        this._keyUpHandler = this._keyUpHandler.bind(this);
    }

    /**
     * Cleanup
     */
    public dispose(): void {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;
        disposeHammers(this._elementsToDispose);
        disposeResizeObserver(this._elementsToDispose);
    }

    /**
     * @returns true if all elements are visible
     */
    public shouldBeHidden(): boolean {
        if (this.getSelectedElementsCount() >= this.getTotalElementsCount()) { return true; }
        return false;
    }

    /**
     * @returns floored selectedElementsCount from options or 1 if none
     */
    public getSelectedElementsCount(): number {
        let count = Math.max(1, getVOFValue(this.options.selectedElementsCount) ?? 1);
        count = Math.floor(count);
        return count;
    }

    /**
     * @returns selectedElementsCount from options or 1 if none (not floored)
     */
    public getFloatSelectedElementsCount(): number {
        const count = Math.max(1, getVOFValue(this.options.selectedElementsCount) ?? 1);
        return count;
    }

    /**
     * @returns totalElementsCount from options or 1 if none
     */
    public getTotalElementsCount(): number {
        return Math.max(1, this.options.totalElementsCount);
    }

    /**
     * Init component
     */
    public override init(): void {
        if (this._isDisposed) {
            // element was disposed => don't build it
            return;
        }
        this._initListeners();
    }

    /** 
     * Refresh scrollbar display
     * @param reason what initiated the refresh
     * @param scrollDivHeight sometimes refresh is called before this.components.scrollDiv.clientHeight is computed, in these cases, we provide the height that will be applied
     */
    public override refresh(reason: string, scrollDivHeight?: number): void {
        if (this._isDisposed) {
            getLogConsole().error("Element is disposed");
            return;
        }

        // debug code => keep commented
        // console.log("scrollbar refresh", reason, "at", this.options.firstDisplayedIndex, "thumb height", this.getFloatSelectedElementsCount(), "total", this.getTotalElementsCount(), "div height", this.components.scrollDiv.clientHeight, "provided height", scrollDivHeight);

        this._moveThumbToFirstDisplayIndexPosition = true;
        this._refreshAndEmitChangeIfNeeded(reason, scrollDivHeight);
    }

    /**
     * refreshAndEmitChangeIfNeeded
     */
    private _refreshAndEmitChangeIfNeeded(reason: string, scrollDivHeight?: number): void {
        this._refreshScrollDivHeight(scrollDivHeight);
        this._emitScrollEventIfFirstIndexChanged();// emit before refreshThumb because the event may change the selected elements count
        this._refreshThumb();
    }

    /** Init listeners of smartScrollbar */
    private _initListeners(): void {
        this.components.scrollDiv.onclick = e => {
            e.stopPropagation(); // Prevent scroll on page
            e.preventDefault();
            this._calculateNewIndex(e.clientY);
        };

        // THUMB
        this.components.thumb.onclick = e => e.stopPropagation();
        this.components.thumb.onmousedown = e => this._handleMouseDownOnThumb(e);

        // Observe resize of component
        if (this.options.observeResize) {
            const resizeObserver = new ResizeObserver(entries => {
                ignoreResizeObserverLoopLimit(entries, () => { this.refresh("resize"); });
            });
            resizeObserver.observe(this);
            this._elementsToDispose.resizeObserver = resizeObserver;
        }

        // Add on wheel event to parent
        const elementToHandle = this.options.elementToHandle ?? this.parentElement;
        if (elementToHandle) {
            elementToHandle.onwheel = e => this._handleWheelEventOnElementToHandle(e);
        }

        // Mobile events
        this.components.thumb.ontouchstart = e => this._handleTouchStartOnThumb(e);

        if (this.options.doNotUseMobilePanEvents?.() || !elementToHandle) { return; }
        // Mobile events with Hammerjs lib
        const parentHam = new Hammer(elementToHandle);
        parentHam.get("pan").set({ direction: Hammer.DIRECTION_ALL });
        parentHam.on("panstart", (e: HammerInput) => {
            this._handlePan(e, "start");
        });
        parentHam.on("panend", (e: HammerInput) => {
            this._handlePan(e, "end");
        });
        parentHam.on("panup", (e: HammerInput) => {
            this._handlePan(e, "up");
        });
        parentHam.on("pandown", (e: HammerInput) => {
            this._handlePan(e, "down");
        });

        // Prevent page movement while pan in smartTable
        elementToHandle.addEventListener("touchstart", (e) => {
            if (this.options.doNotUseMobilePanEvents?.()) { return; }
            e.preventDefault();
        });

        const containerHam = new Hammer(this.components.scrollDiv!);
        containerHam.on("tap", (e: HammerInput) => {
            if (e.pointerType === "mouse" || this.options.doNotUseMobilePanEvents?.()) { return; }
            e.srcEvent.stopPropagation();
            e.srcEvent.preventDefault();
            this._calculateNewIndex(e.center.y);
        });
        registerHammerForDisposal(this._elementsToDispose, parentHam, containerHam);
    }

    /**
     * install keyboard listeners on component
     */
    public installKeyListeners(component: HTMLElement): void {
        component.addEventListener("keydown", this._keyDownHandler);
        component.addEventListener("keyup", this._keyUpHandler);
    }

    /**
     * uninstall keyboard listeners on component
     */
    protected uninstallKeyListeners(component: HTMLElement): void {
        component.removeEventListener("keydown", this._keyDownHandler);
        component.removeEventListener("keyup", this._keyUpHandler);
    }

    /**
     * @returns the theoretical thumb height if there is exactly one element visible
     */
    private _getOneElementThumbHeight(): number {
        return this._scrollDivHeight / this.getTotalElementsCount();
    }

    /** Refresh scrollDivHeight value */
    private _refreshScrollDivHeight(scrollDivHeight?: number): void {
        // sometimes refresh is called before this.components.scrollDiv.clientHeight is computed, in these cases, we provide the height that will be applied
        this._scrollDivHeight = scrollDivHeight ?? this.components.scrollDiv.clientHeight;
    }

    /** Refresh scrollDivHeight value */
    private _refreshThumb(): void {
        this._refreshThumbHeight();

        // We set _moveThumbToFirstDisplayIndexPosition true to moveThumb to firstDisplayIndex position
        if (this._moveThumbToFirstDisplayIndexPosition) {
            this._refreshThumbTop();
            this._moveThumbToFirstDisplayIndexPosition = false;
        }
    }


    /**
     * Calculate the thumb top based on firstDisplayedIndex
     */
    private _refreshThumbTop(): void {
        const newElementsNumber = NumberTools.isNumeric(this.options.firstDisplayedIndex) && this.options.firstDisplayedIndex > 0 ? this.options.firstDisplayedIndex : undefined;
        if (newElementsNumber !== undefined) {
            const newThumbTop = this._getOneElementThumbHeight() * newElementsNumber;
            this._moveThumb(newThumbTop);
        } else {
            this.resetThumb();
        }
    }

    /**
     * Calculate the thumb height based on selected / total number elements
     */
    private _refreshThumbHeight(): void {
        const containerHeight = this._scrollDivHeight;
        let height = containerHeight * this.getFloatSelectedElementsCount() / this.getTotalElementsCount();
        height = Math.max(this.options.minHeight, height);
        height = Math.min(height, containerHeight);

        // If no element for scrollbar, set height of thumb to 0 <=> hide it
        if (this.options.totalElementsCount === 0) {
            height = 0;
        }

        // Check if height of thumb has changed
        const heightInPx = height + "px";
        if (this.components.thumb.style.height !== heightInPx) {
            this.components.thumb.style.height = heightInPx;
            this._thumbHeight = height;
        }

    }

    /**
     * Function that emits the new index for parent to refresh its displayed elements
     */
    private _emitScrollEventIfFirstIndexChanged(): void {

        if (this.getSelectedElementsCount() > this.getTotalElementsCount()) { return; }

        // Don't refresh display if first displayed index don't change
        if (this._previousFirstDisplayedIndex === this.options.firstDisplayedIndex) { return; }

        if (this._canWheelEventScroll) {
            this._canWheelEventScroll = false;
            setTimeout(() => {

                this._canWheelEventScroll = true;

                // Case : smartScrollBar with not initialize() called
                //        => ScrollBar here but not visible, nothing to do
                if (!isFinite(this.options.firstDisplayedIndex)) {
                    return;
                }

                const detail: SmartScrollBarOnScrollEvent = {
                    index: this.options.firstDisplayedIndex
                };

                // Dispatch event
                this.refreshIndexEvent.emit(detail);

                // Update previous index displayed
                this._previousFirstDisplayedIndex = this.options.firstDisplayedIndex;

            }, this.options.wheelScrollEventDebounce);
        }
    }

    /**
     * Move thumb inside scroll div
     * @param newTop // Number of pixels that represents the wished top style attribute of the thumb
     */
    private _moveThumb(newTop: number): void {
        if (this.options.firstDisplayedIndex === this.getTotalElementsCount() - this.getSelectedElementsCount()) {
            // we have reached the bottom
            newTop = Math.max(this._scrollDivHeight - this._thumbHeight, 0);
            this.components.thumb.style.top = String(newTop) + "px";
            return;
        } else if (this.options.firstDisplayedIndex === 0) {
            // we have reached the top
            newTop = 0;
            this.components.thumb.style.top = String(newTop) + "px";
            return;
        }

        newTop = Math.min(newTop, this._scrollDivHeight - this._thumbHeight);
        newTop = Math.max(newTop, 0);
        this.components.thumb.style.top = String(newTop) + "px";
    }

    /**
     * Calculate new Index based on mouse/touch events Y coordinates
     * @param pos // Y coordinates in pixels from event
     */
    private _calculateNewIndex(pos: number): void {
        const yPos = Math.floor(pos - this.components.scrollDiv.getBoundingClientRect().top);
        let newIndex = yPos * this.getTotalElementsCount() / this._scrollDivHeight;
        newIndex = Math.max(newIndex, 0);
        newIndex = Math.min(newIndex, this.getTotalElementsCount() - this.getSelectedElementsCount());
        this.options.setWithoutRefresh({ firstDisplayedIndex: Math.floor(newIndex) });

        this._refreshAndEmitChangeIfNeeded("mouse_drag");
    }

    /**
     * It binds a mousemove event on the scroll div to update scrollbar position accordingly.
     * It calls the CalculateNewIndex function.
     * Eventually, it unbinds events that were bind to the scroll div.
     * @param event // Mouse event from the mouseDown on thumb
     */
    private _handleMouseDownOnThumb(event: MouseEvent): void {
        event.preventDefault();

        // On mouse move
        const onMouseMove = (e: MouseEvent): void => {
            // Check if left button is still pressed (can be released outer browser window). If not pressed, remove listener
            if (e.buttons !== 1) {
                removeMouseMove();
                return;
            }

            e.preventDefault();
            this._calculateNewIndex(e.clientY);
        };
        document.body.addEventListener("mousemove", onMouseMove);

        const removeMouseMove: () => void = () => {
            document.body.removeEventListener("mousemove", onMouseMove);
        };

        // On mouse up, remove mouse move listener
        document.body.addEventListener("mouseup", (e: MouseEvent): void => {
            removeMouseMove();
        }, { once: true });

    }

    /**
     * Prevent default event w
     * @param event // Keyboard event issued from keydown 
     */
    private _keyDownHandler(event: KeyboardEvent): void {
        const allowedKeys: string[] = [KeyboardInput.PAGE_DOWN, KeyboardInput.PAGE_UP];
        if (!allowedKeys.includes(event.key)) { return; }
        event.preventDefault();
    }

    /**
     * Handle keyBoard press event to update parent element and thumb position
     * @param event // Keyboard event issued from keyup
     */
    private _keyUpHandler(event: KeyboardEvent): void {

        if (event.key === KeyboardInput.PAGE_DOWN) { // Page down key (scroll down)
            if (this.options.firstDisplayedIndex >= this.getTotalElementsCount() - this.getSelectedElementsCount()) { return; }
            this.options.setWithoutRefresh({ firstDisplayedIndex: this.options.firstDisplayedIndex + this.getSelectedElementsCount() });
        } else if (event.key === KeyboardInput.PAGE_UP) { // Page up key (scroll up)
            if (this.options.firstDisplayedIndex <= 0) { return; }
            this.options.setWithoutRefresh({ firstDisplayedIndex: Math.max(0, this.options.firstDisplayedIndex - this.getSelectedElementsCount()) });
        } else {
            return;
        }

        event.preventDefault();
        this._refreshAndEmitChangeIfNeeded("key_up");
    }

    /**
     * Handle wheel event on Parent, it changes the firstDisplayedIndex value according to the scroll direction (UP/DOWN).
     * It also updates the position of the scroll bar according to the new value of firstDisplayedIndex.
     * @param event // Wheel event containing the scrolling direction
     */
    private _handleWheelEventOnElementToHandle(event: WheelEvent): void {

        if (this.style.display === Display.NONE) {
            return;
        }

        if (event.shiftKey) {
            // Horizontal scroll, leave default handler
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const scrollFactor = 1;// This var can be used to debug components response to big scroll, it should stay 1 in the "master"

        if (event.deltaY > 0) {
            if (this.options.firstDisplayedIndex >= this.getTotalElementsCount() - this.getSelectedElementsCount()) { return; }
            this.options.setWithoutRefresh({ firstDisplayedIndex: this.options.firstDisplayedIndex + scrollFactor });
        } else {
            if (this.options.firstDisplayedIndex <= 0) { return; }
            this.options.setWithoutRefresh({ firstDisplayedIndex: Math.max(0, this.options.firstDisplayedIndex - scrollFactor) });
        }

        this._refreshAndEmitChangeIfNeeded("wheel_scroll");
    }

    /**
     * It binds a touchmove event on the scroll div to update scrollbar position accordingly.
     * It calls the CalculateNewIndex function.
     * Eventually, it unbinds events that were bind to the scroll div.
     * @param event // Touch event issued from touchstart on thumb
     */
    private _handleTouchStartOnThumb(event: TouchEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.components.scrollDiv.ontouchmove = (e: TouchEvent) => {
            e.stopPropagation();
            this._calculateNewIndex(e.touches[0].clientY);
        };
        this.components.scrollDiv.ontouchend = (e: TouchEvent) => {
            e.preventDefault();
            this.components.scrollDiv.ontouchend = this.components.scrollDiv.ontouchmove = this.components.scrollDiv.ontouchcancel = null;
        };
        this.components.scrollDiv.ontouchcancel = (e: TouchEvent) => {
            e.preventDefault();
            this.components.scrollDiv.ontouchcancel = this.components.scrollDiv.ontouchmove = this.components.scrollDiv.ontouchend = null;
        };
    }

    protected _panInProgress: {
        initialDisplayIndex: number,
        firstEvent: HammerInput
    } | undefined = undefined;


    /** Handle pan, Hammerjs event 
     * @param e // HammerInput event from pan up or down
     */
    protected _handlePan(e: HammerInput, what: "up" | "down" | "start" | "end"): void {
        if (what === "end") {
            // cleanup
            this._panInProgress = undefined;
            return;
        }

        const parentElement = this.options.elementToHandle ?? this.parentElement;
        const smartTable = parentElement ? findNearestMatch(parentElement, (elt) => elt instanceof CjsSmartTable) as CjsSmartTable<unknown> : undefined;
        if (smartTable == null) {
            // we handle pan only for the smartTable for now
            return;
        }
        if (e.pointerType === "mouse" || this.options.doNotUseMobilePanEvents?.()) { return; }

        try {
            if (!this._panInProgress || what === "start") {
                this._panInProgress = {
                    initialDisplayIndex: this.options.firstDisplayedIndex,
                    firstEvent: e
                };
                if (what === "start") {
                    // nothing more to do
                    return;
                }
            }

            e.srcEvent.preventDefault();
            e.srcEvent.stopPropagation();

            let offset = e.deltaY;
            let targetDisplayIndex = this._panInProgress.initialDisplayIndex;
            while (true && offset) {
                const row = smartTable.displayableRows[targetDisplayIndex];
                if (row == null) {
                    // reached and edge
                    break;
                }
                const rowHeight = smartTable.getRowHeight(row);
                if (offset < 0) {
                    // going up
                    if (-offset < rowHeight) {
                        // reached the target
                        break;
                    } else {
                        offset += rowHeight;
                        targetDisplayIndex++;
                    }
                } else {
                    // going down
                    if (offset < rowHeight) {
                        // reached the target
                        break;
                    } else {
                        offset -= rowHeight;
                        targetDisplayIndex--;
                    }
                }
            }

            const bottomIndex = this.getTotalElementsCount() - this.getSelectedElementsCount();
            const newFirstDisplayedIndex = Math.max(Math.min(bottomIndex, targetDisplayIndex), 0);
            this.options.setWithoutRefresh({ firstDisplayedIndex: newFirstDisplayedIndex });
            this._refreshAndEmitChangeIfNeeded("pan");

        } catch (error) {
            getLogConsole().error(error);
        }
    }

    /**
     * Add targets to make scroll on target scrolling scrollbar too
     * @param targets htmlElements or their ref attribute to listen for onwheel event
     * @returns 
     */
    public addScrollTargets(targets: AcceptedHtmlElementContent[]): void {
        if (targets.length) {
            for (const target of targets) {
                if (target instanceof HTMLElement) {
                    target.addEventListener("wheel", e => this._handleWheelEventOnElementToHandle(e), { passive: false });
                } else {
                    const elem = findElementByRef(document.body, target);
                    if (elem) {
                        elem.addEventListener("wheel", e => this._handleWheelEventOnElementToHandle(e), { passive: false });
                    }
                }
            }
        }
    }

    /**
     * Reset thumb position to the beginning
     */
    public resetThumb(): void {
        this._moveThumb(0);
    }
}